import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { useStores } from "../../hooks/useStores";
import { ReservationText } from "./styled";
import { observer } from "mobx-react";
import { CalendlyEventListener, openPopupWidget } from "react-calendly";
import {
  Button,
  Stack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Image,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogCloseButton,
  AlertDialogBody,
  AlertDialogFooter,
} from "@chakra-ui/react";
import { ReservationCard } from "../ReservationCard";
import { Header } from "../../components/Header";
import { useEffect } from "react";
import { ReservationModal } from "../ReservationModal";
import { useDisclosure } from "@chakra-ui/hooks";
import { BookingEvents, Reservation } from "../../types";
import dayjs from "dayjs";

type Props = {
  isTrialExpired?: boolean;
  isSubscriptionDeactivated?: boolean;
  handleDrawer: () => void;
  onCloseDrawer?: () => void;
  isMobile?: boolean;
  subscriptionType: string;
};

export enum MenuItemKeys {
  board,
  bookCoachingSession,
  liveStreamingSession,
  longLiveStreamingSession,
  bookPersonalTrainerActiveSubscription = "CALENDLY_BOOK_PERSONAL_TRAINER_ACTIVE_SUBSCRIPTION",
  bookPersonalTrainerInactiveSubscription = "CALENDLY_BOOK_PERSONAL_TRAINER_INACTIVE_SUBSCRIPTION",
}

export const ClientSidebar: React.FC<Props> = observer(
  ({
    isTrialExpired = false,
    handleDrawer,
    onCloseDrawer,
    isMobile = false,
    isSubscriptionDeactivated = false,
    subscriptionType,
  }) => {
    const { t } = useTranslation();
    const { session } = useStores();
    const history = useHistory();
    const { isOpen, onClose, onOpen } = useDisclosure();
    const [selectedReservation, setSelectedReservation] =
      useState<Reservation | null>(null);
    const {
      isOpen: isModalTimetableOpen,
      onOpen: onOpenModalTimetable,
      onClose: onCloseModalTimetable,
    } = useDisclosure();
    const DAYS_OF_WEEK = [0, 1, 2, 3, 4, 5, 6];
    const {
      isOpen: isOpenExpiredDialog,
      onOpen: onExpierdDialogOpen,
      onClose: onExpierdDialogClose,
    } = useDisclosure();
    const cancelExpiredDialogRef = React.useRef();
    const [activeBookingEvent, setActiveBookingEvent] = useState<
      BookingEvents | undefined
    >(undefined);

    useEffect(() => {
      session.fetchReservations();
    }, []);

    const handleNavigateTo = useCallback(
      (key) => {
        switch (key) {
          case MenuItemKeys.bookCoachingSession:
            if (isMobile) {
              history.push("/client/book-coaching-session");
            } else {
              openPopupWidget({
                url: session.calendlySessionCoachingUrl?.value || "",
                prefill: {
                  name: `${session.user?.name} ${session.user?.surname}`,
                  firstName: session.user?.name,
                  lastName: session.user?.surname,
                  email: session.user?.email,
                },
              });
            }
            break;
          case MenuItemKeys.liveStreamingSession:
            if (isMobile) {
              history.push("/client/book-live-workout");
            } else {
              openPopupWidget({
                url: session.calendlyLiveTrainingUrl?.value || "",
                prefill: {
                  name: `${session.user?.name} ${session.user?.surname}`,
                  firstName: session.user?.name,
                  lastName: session.user?.surname,
                  email: session.user?.email,
                },
              });
            }
            break;
          case MenuItemKeys.bookPersonalTrainerActiveSubscription:
            if (isMobile) {
              history.push("/client/book-personal-trainer");
            } else {
              setActiveBookingEvent(
                BookingEvents.BOOK_PERSONAL_TRAINER_ACTIVE_SUBSCRIPTION
              );
              openPopupWidget({
                url:
                  session.calendlyBookPersonalTrainerActiveSubscription
                    ?.value || "",
                prefill: {
                  name: `${session.user?.name} ${session.user?.surname}`,
                  firstName: session.user?.name,
                  lastName: session.user?.surname,
                  email: session.user?.email,
                },
              });
            }
            break;
          case MenuItemKeys.bookPersonalTrainerInactiveSubscription:
            if (isMobile) {
              history.push("/client/book-personal-trainer-trial");
            } else {
              setActiveBookingEvent(
                BookingEvents.BOOK_PERSONAL_TRAINER_INACTIVE_SUBSCRIPTION
              );
              openPopupWidget({
                url:
                  session.calendlyBookPersonalTrainerInctiveSubscription
                    ?.value || "",
                prefill: {
                  name: `${session.user?.name} ${session.user?.surname}`,
                  firstName: session.user?.name,
                  lastName: session.user?.surname,
                  email: session.user?.email,
                },
              });
            }
            break;
        }
      },
      [session, isMobile, history]
    );

    const onEventScheduled = useCallback(
      async (event: MessageEvent) => {
        console.log(event);
        const data: {
          event: string; // calendly.event_scheduled
          payload: {
            event: {
              uri: string; // "https://api.calendly.com/scheduled_events/CKAFHPPSNOW7Y4VL"
            };
            invitee: {
              uri: string; // "https://api.calendly.com/scheduled_events/CKAFHPPSNOW7Y4VL/invitees/HLCRWVIVCQNTSSUG"
            };
          };
        } = event.data;

        await session.traceReservation({
          dateTime: new Date(), // temp. date
          externalId: data.payload.event.uri.split("/").pop(), // Calendly event ID
          source: "calendly",
          type: data.event,
          eventMeta: data.payload,
          bookingEvent: activeBookingEvent,
        });

        setActiveBookingEvent(undefined);
      },
      [activeBookingEvent, session]
    );

    const handleReservationClick = useCallback(
      (reservation: Reservation) => {
        setSelectedReservation(reservation);
        onOpen();
      },
      [onOpen]
    );

    const handleDeleteReservation = useCallback((reservation: Reservation) => {
      // Open Calendaly to delete the event.
      const cancelURL =
        reservation.eventMeta.cancel_url ||
        // Calendly cancel link = calendly invitee ID (get from uri "https://api.calendly.com/scheduled_events/HIFNXPLSXFK54GCS/invitees/FMLDPI42MCWLKHCV" => FMLDPI42MCWLKHCV )
        `https://calendly.com/cancellations/${reservation.eventMeta.invitee.uri
          .split("/")
          .pop()}`;

      window.open(cancelURL, "_blank");

      setSelectedReservation(null);
    }, []);

    const handleRescheduleReservation = useCallback(
      (reservation: Reservation) => {
        const rescheduleURL = reservation.eventMeta.reschedule_url;
        if (rescheduleURL) {
          window.open(rescheduleURL, "_blank");
        }
        setSelectedReservation(null);
      },
      []
    );

    // https://mostaza.atlassian.net/browse/SAFW-59
    const handlePersonalTrainerBooking = useCallback(() => {
      if (
        isSubscriptionDeactivated ||
        isTrialExpired ||
        subscriptionType === "PT"
      ) {
        handleNavigateTo(MenuItemKeys.bookPersonalTrainerInactiveSubscription);
      } else {
        handleNavigateTo(MenuItemKeys.bookPersonalTrainerActiveSubscription);
      }

      if (onCloseDrawer) onCloseDrawer();
    }, [
      handleNavigateTo,
      isSubscriptionDeactivated,
      isTrialExpired,
      onCloseDrawer,
      subscriptionType,
    ]);

    return (
      <Stack
        // overflow={"scroll"}
        height={"100%"}
        w="320px"
        borderLeft="1px solid #E5E5E5"
        paddingLeft={"2rem"}
        paddingRight={"1rem"}
        paddingTop={"2rem"}
      >
        <Header
          user={session.user}
          onLogout={() => session.resetSession()}
          onNavigateToProfile={() => {
            history.push("/client/profile");
            if (onCloseDrawer) onCloseDrawer();
          }}
        />

        <Stack mt={"0.5rem !important"} gridGap={"0.1rem"}>
          <Stack mb={"1rem !important"}>
            <Button
              size="lg"
              borderRadius="16px"
              translation="common.confirm"
              onSubmit={() => {}}
              w="100%"
              border="2px"
              pt="8px"
              pb="8px"
              fontSize="14px"
              bg="#ffffff"
              borderColor="#562D5F"
              color="#562D5F"
              _hover={{ bg: "#562D5F", color: "#ffffff" }}
              onClick={onOpenModalTimetable}
            >
              Orari allenamenti con supervisione
            </Button>

            <Button
              size="lg"
              mt="25px"
              type={"submit"}
              borderRadius="16px"
              translation="common.confirm"
              onSubmit={() => {}}
              w="100%"
              border="2px"
              pt="8px"
              pb="8px"
              fontSize="14px"
              bg="#ffffff"
              borderColor={
                isTrialExpired ||
                isSubscriptionDeactivated ||
                subscriptionType === "PT"
                  ? "#CDCDCD"
                  : "#562D5F"
              }
              color={
                isTrialExpired ||
                isSubscriptionDeactivated ||
                subscriptionType === "PT"
                  ? "#CDCDCD"
                  : "#562D5F"
              }
              _hover={
                isTrialExpired ||
                isSubscriptionDeactivated ||
                subscriptionType === "PT"
                  ? { bg: "#fff", color: "#CDCDCD" }
                  : { bg: "#562D5F", color: "#ffffff" }
              }
              onClick={() => {
                if (
                  isTrialExpired ||
                  isSubscriptionDeactivated ||
                  subscriptionType === "PT"
                ) {
                  onExpierdDialogOpen();
                } else {
                  history.push("/client/long-live-training");
                  if (onCloseDrawer) onCloseDrawer();
                }
              }}
            >
              Accesso allenamento in diretta
              <br /> con supervisione
            </Button>

            <Button
              size="lg"
              borderRadius="16px"
              mt="25px"
              w="100%"
              border="2px"
              pt="8px"
              pb="8px"
              bg="#ffffff"
              borderColor={
                isTrialExpired || isSubscriptionDeactivated
                  ? "#CDCDCD"
                  : "#562D5F"
              }
              color={
                isTrialExpired || isSubscriptionDeactivated
                  ? "#CDCDCD"
                  : "#562D5F"
              }
              _hover={
                isTrialExpired || isSubscriptionDeactivated
                  ? { bg: "#fff", color: "#CDCDCD" }
                  : { bg: "#562D5F", color: "#ffffff" }
              }
              fontSize="0.85rem"
              onClick={() => {
                if (isTrialExpired || isSubscriptionDeactivated) {
                  onExpierdDialogOpen();
                } else {
                  history.push("/client/video-categories");
                  if (onCloseDrawer) onCloseDrawer();
                }
              }}
            >
              {t("screens.client.customer.onlineTraning")}
            </Button>

            <Button
              size="lg"
              mt="25px"
              type={"submit"}
              borderRadius="16px"
              translation="common.confirm"
              onSubmit={() => {}}
              w="100%"
              border="2px"
              pt="8px"
              pb="8px"
              fontSize="14px"
              bg="#ffffff"
              borderColor={
                isTrialExpired ||
                isSubscriptionDeactivated ||
                subscriptionType === "PT"
                  ? "#CDCDCD"
                  : "#562D5F"
              }
              color={
                isTrialExpired ||
                isSubscriptionDeactivated ||
                subscriptionType === "PT"
                  ? "#CDCDCD"
                  : "#562D5F"
              }
              _hover={
                isTrialExpired ||
                isSubscriptionDeactivated ||
                subscriptionType === "PT"
                  ? { bg: "#fff", color: "#CDCDCD" }
                  : { bg: "#562D5F", color: "#ffffff" }
              }
              onClick={() => {
                if (
                  isTrialExpired ||
                  isSubscriptionDeactivated ||
                  subscriptionType === "PT"
                ) {
                  onExpierdDialogOpen();
                } else {
                  handleNavigateTo(MenuItemKeys.bookCoachingSession);
                  if (onCloseDrawer) onCloseDrawer();
                }
              }}
            >
              Prenotazione coaching individuale
            </Button>

            <Button
              size="lg"
              mt="25px"
              type={"submit"}
              borderRadius="16px"
              translation="common.confirm"
              onSubmit={() => {}}
              w="100%"
              border="2px"
              pt="8px"
              pb="8px"
              fontSize="14px"
              bg="#ffffff"
              borderColor="#562D5F"
              color="#562D5F"
              _hover={{ bg: "#562D5F", color: "#ffffff" }}
              onClick={handlePersonalTrainerBooking}
            >
              Prenotazione Personal Trainer
            </Button>

            <Button
              size="lg"
              mt="25px"
              type={"submit"}
              borderRadius="16px"
              translation="common.confirm"
              onSubmit={() => {}}
              w="100%"
              border="2px"
              pt="8px"
              pb="8px"
              fontSize="14px"
              bg="#ffffff"
              borderColor="#562D5F"
              color="#562D5F"
              _hover={{ bg: "#562D5F", color: "#ffffff" }}
              onClick={() => {
                history.push("/client/objectives");
                if (onCloseDrawer) onCloseDrawer();
              }}
            >
              Obiettivi e Fattori X
            </Button>

            <Button
              size="lg"
              borderRadius="16px"
              w="100%"
              border="2px"
              pt="8px"
              pb="8px"
              bg="#ffffff"
              borderColor="#562D5F"
              color="#562D5F"
              fontSize="0.85rem"
              _hover={{ bg: "#562D5F", color: "#ffffff" }}
              onClick={() => {
                history.push("/client/evaluations");
                if (onCloseDrawer) onCloseDrawer();
              }}
            >
              {t("screens.client.customer.fillMonthlyEvaluation")}
            </Button>
          </Stack>

          <Stack>
            {session.reservations.length > 0 && (
              <ReservationText>Prossime attività</ReservationText>
            )}
            {session.reservations
              .slice()
              .sort((a, b) => a.dateTime.getTime() - b.dateTime.getTime())
              .map((reservation) => (
                <ReservationCard
                  reservation={reservation}
                  onClick={handleReservationClick}
                />
              ))}
          </Stack>

          <CalendlyEventListener onEventScheduled={onEventScheduled} />

          {selectedReservation && (
            <ReservationModal
              isOpen={isOpen}
              onClose={onClose}
              reservation={selectedReservation}
              onCancel={handleDeleteReservation}
              onReschedule={handleRescheduleReservation}
            />
          )}

          <Modal
            isOpen={isModalTimetableOpen}
            onClose={onCloseModalTimetable}
            size="3xl"
          >
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Orari allenamenti con supervisione</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Image src={session.worksoutTimetable?.value} alt="Orari" />
              </ModalBody>
            </ModalContent>
          </Modal>
        </Stack>

        <AlertDialog
          motionPreset="slideInBottom"
          // @ts-ignore
          leastDestructiveRef={cancelExpiredDialogRef}
          onClose={onExpierdDialogClose}
          isOpen={isOpenExpiredDialog}
          isCentered
        >
          <AlertDialogOverlay />

          <AlertDialogContent>
            <AlertDialogHeader>Avviso</AlertDialogHeader>
            <AlertDialogCloseButton />
            <AlertDialogBody>
              Per accedere ai servizi Sabeda attiva l'abbonamento ora e cancelli
              quando vuoi!
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                /* @ts-ignore */
                ref={cancelExpiredDialogRef}
                onClick={onExpierdDialogClose}
              >
                Non adesso
              </Button>
              <Button
                colorScheme="purple"
                ml={3}
                onClick={session.stripeCheckout}
              >
                Attiva ora
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Stack>
    );
  }
);
